import { ageGuard, notAgeGuard } from "@/router/ageGuard";
import { authGuard, loginCleanup, notAuthGuard } from "@/router/authGuard";
import Vue from "vue";
import VueRouter, { Route } from "vue-router";
import Welcome from "@/views/Welcome.vue";

import { authClient } from "@/auth0";
import {
  setCategoryView,
  setContent,
  setFeed,
  setPurchases,
  setUser
} from "@/router/data";
import multiguard from "vue-router-multiguard";
import store from "@/store";

Vue.use(VueRouter);

const defaultComponent: any = { render: (h: any) => h("router-view") };

const routes = [
  {
    // Redirects a user either to the internal area if they are logged in or otherwise to the welcome page.
    path: "/",
    name: "root",
    beforeEnter: async (to: Route, from: Route, next: Function) => {
      // Check weather a login is in progress
      const isLoggedIn = await authClient.getIsAuthenticated();

      console.debug(`[ROUTE] - [/] - Is the user logged in? ${isLoggedIn}`);

      const nextPage: any = {
        path: isLoggedIn ? "/feed" : "/welcome",
        query: from.query
      };

      next(nextPage);
    }
  },
  {
    // Default entry point for the website. Only visible if the user is NOT logged in.
    path: "",
    component: defaultComponent,
    children: [
      {
        path: "/welcome",
        name: "welcome",
        component: Welcome
      },
      {
        path: "/login",
        name: "login",
        component: () =>
          import(/* webpackChunkName: "login" */ "../views/Login.vue")
      }
    ]
  },
  {
    // All internal routes are grouped here
    path: "",
    beforeEnter: multiguard([authGuard, setUser]),
    component: defaultComponent,
    children: [
      {
        path: "/verify_age",
        name: "verify_age",
        beforeEnter: multiguard([notAgeGuard]),
        component: () =>
          import(
            /* webpackChunkName: "verify_age" */ "../views/AgeVerification.vue"
          )
      },
      {
        // All routes that require a positive age verification
        path: "",
        beforeEnter: multiguard([ageGuard]),
        component: defaultComponent,
        children: [
          {
            path: "/feed",
            name: "home",
            component: () =>
              import(/* webpackChunkName: "feed" */ "../views/Feed.vue"),
            beforeEnter: multiguard([setFeed])
          },
          {
            path: "/categories/:category",
            name: "categories",
            component: () =>
              import(
                /* webpackChunkName: "categories" */ "../views/Categories.vue"
              ),
            beforeEnter: multiguard([setCategoryView])
          },
          { path: "/categories", redirect: "/categories/all" },
          {
            path: "/content/:id",
            name: "content",
            component: () =>
              import(
                /* webpackChunkName: "content" */ "../views/ContentDetails.vue"
              ),
            beforeEnter: multiguard([setContent])
          },
          {
            path: "/purchases",
            name: "purchases",
            component: () =>
              import(
                /* webpackChunkName: "purchases" */ "../views/Purchases.vue"
              ),
            beforeEnter: multiguard([setPurchases])
          },
          {
            path: "/charge",
            name: "charge",
            component: () =>
              import(/* webpackChunkName: "charge" */ "../views/Charge.vue"),
            beforeEnter: multiguard([])
          },
          {
            path: "/settings",
            name: "settings",
            component: () =>
              import(
                /* webpackChunkName: "settings" */ "../views/Settings.vue"
              ),
            beforeEnter: multiguard([])
          }
        ]
      }
    ]
  },
  {
    path: "/sofortident",
    name: "sofortident",
    component: () =>
      import(
        /* webpackChunkName: "sofortident" */ "../views/SofortIdentCallback.vue"
      )
  },
  {
    path: "/logout",
    name: "logout",
    beforeEnter: async (to: Route, from: Route, next: Function) => {
      await authClient.logout();
    }
  },
  {
    path: "/404",
    name: "404",
    component: () => import(/* webpackChunkName: "404" */ "../views/404.vue")
  },
  {
    path: "/jugendschutz",
    name: "jugendschutz",
    component: () =>
      import(
        /* webpackChunkName: "jugendschutz" */ "../views/static/jugendschutz.vue"
      )
  },
  {
    path: "/impressum",
    name: "impress",
    component: () =>
      import(/* webpackChunkName: "impress" */ "../views/static/impress.vue")
  },
  {
    path: "/datenschutz",
    name: "privacy",
    component: () =>
      import(/* webpackChunkName: "privacy" */ "../views/static/privacy.vue")
  },
  {
    path: "/agb",
    name: "tos",
    component: () =>
      import(/* webpackChunkName: "tos" */ "../views/static/tos.vue")
  },
  { path: "*", redirect: "/404" }
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes: routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  }
});

router.beforeEach(
  multiguard([
    loginCleanup,
    (to: Route, from: Route, next: Function) => {
      store.commit("basic", {
        key: "cannotLoadPage",
        value: false
      });
      return next();
    }
  ])
);

export default router;
